import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { Spinner } from "@/components/site/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fetchCourse } from "@/lib/course";
import { useSeo } from "@/lib/seo";
import { statusLabel } from "@/lib/constants";

function Q({ prompt, value, filled, tall }) {
  return (
    <div className="print-q">
      <h4>{prompt}</h4>
      {filled ? <div className="print-answer">{value || " "}</div> : <div className={`print-lines ${tall ? "tall" : ""}`} />}
    </div>
  );
}

function SheetHeader({ title, sub, name }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b-2 border-[#0B2A4A] pb-3 mb-5">
      <div>
        <p className="font-mono-ui text-[9pt] tracking-widest text-[#147C88]">DIGITAL NOMAD NICARAGUA · BECOME A DIGITAL NOMAD FOR BEGINNERS</p>
        <h2 className="text-[17pt] font-bold text-[#0B2A4A] mt-1 leading-tight">{title}</h2>
        {sub && <p className="text-[10pt] text-slate-600 mt-1">{sub}</p>}
      </div>
      <div className="text-right text-[9.5pt] shrink-0">
        <img src="/brand/monogram.png" alt="" className="h-9 ml-auto" />
        <p className="mt-1">Name: {name || "______________________"}</p>
      </div>
    </div>
  );
}

function Worksheet({ ws, answers, filled, name, breakBefore }) {
  return (
    <section className={breakBefore ? "print-break" : ""} data-testid={`print-worksheet-${ws.id}`}>
      <SheetHeader title={`Worksheet ${ws.number} — ${ws.title}`} sub={ws.intro} name={name} />
      {ws.fields.map((f) => <Q key={f.id} prompt={f.prompt} value={answers?.[f.id]} filled={filled} />)}
    </section>
  );
}

function DaySheet({ day, ws, rec, filled, name }) {
  const a = rec?.answers || {};
  return (
    <>
      <section>
        <SheetHeader title={`Day ${day.day} — ${day.title}`} sub={`Outcome: ${day.outcome}`} name={name} />
        {filled && <p className="text-[10pt] mb-3"><strong>Status:</strong> {statusLabel(rec?.status)}</p>}
        <h3 className="text-[12.5pt] font-bold text-[#0B2A4A] mb-2">Questions participants answer in class</h3>
        {day.class_questions.map((q) => <Q key={q.id} prompt={q.prompt} value={a[q.id]} filled={filled} />)}
        <Q prompt="Question for the host" value={a.host_question} filled={filled} />
        <h3 className="text-[12.5pt] font-bold text-[#0B2A4A] mt-4 mb-2 print-q">Homework to complete at home</h3>
        <p className="text-[10.5pt] mb-3 print-q">{day.homework}</p>
        {day.homework_questions.map((q) => <Q key={q.id} prompt={q.prompt} value={a[q.id]} filled={filled} />)}
        <Q prompt="Evidence: sources, dates, links, or notes" value={a.homework_evidence} filled={filled} />
        <div className="print-q mt-4">
          <h3 className="text-[12.5pt] font-bold text-[#0B2A4A] mb-2">Deliverables to save to the student profile</h3>
          <ul className="text-[10.5pt] space-y-1">
            {day.deliverables.map((d) => <li key={d.id}>{filled && rec?.deliverables?.[d.id] ? "☑" : "☐"} {d.label}</li>)}
          </ul>
        </div>
      </section>
      {ws && <Worksheet ws={ws} answers={a} filled={filled} name={name} breakBefore />}
    </>
  );
}

function HostPlanDay({ day, breakBefore }) {
  const g = day.host_guide;
  return (
    <section className={breakBefore ? "print-break" : ""} data-testid={`print-host-day-${day.day}`}>
      <SheetHeader title={`Host lesson plan · Day ${day.day} — ${day.title}`} sub={day.purpose} name="Host" />
      <div className="text-[10.5pt] space-y-2">
        <p className="print-q"><strong>Outcome:</strong> {day.outcome}</p>
        <p className="print-q"><strong>Opening question:</strong> {day.opening_question}</p>
        <p className="print-q"><strong>Host example:</strong> {day.host_example}</p>
        <div className="print-q"><strong>Teach these points:</strong><ul className="list-disc pl-5 mt-1 space-y-1">{day.teach_points.map((t) => <li key={t}>{t}</li>)}</ul></div>
      </div>
      <h3 className="text-[12.5pt] font-bold text-[#0B2A4A] mt-4 mb-2">Live 60-minute flow</h3>
      <table className="print-table">
        <thead><tr><th style={{ width: "11%" }}>Time</th><th>Host action</th><th>Teaching point</th><th>Ask the room</th><th>Participants do</th></tr></thead>
        <tbody>{day.flow.map((r) => <tr key={`${r.time}-${r.host_action}`}><td>{r.time}</td><td>{r.host_action}</td><td>{r.teach}</td><td>{r.ask}</td><td>{r.do}</td></tr>)}</tbody>
      </table>
      <div className="text-[10.5pt] space-y-2 mt-4">
        <p className="print-q"><strong>In-person activity:</strong> {day.in_person_activity}</p>
        {g.practice_lab.map((t) => <p key={t} className="print-q">{t}</p>)}
        <div className="print-q"><strong>Close and take-home action:</strong>{g.close.map((t) => <p key={t} className="mt-1">{t}</p>)}</div>
        <div className="print-q"><strong>Class questions:</strong> {day.class_questions.map((q) => q.prompt).join(" · ")}</div>
        <p className="print-q"><strong>Homework:</strong> {day.homework}</p>
        <p className="print-q"><strong>Deliverables:</strong> {day.deliverables.map((d) => d.label).join(" · ")}</p>
      </div>
    </section>
  );
}

function Reference({ sections }) {
  return sections.map((s) => (
    <section key={s.heading} className="print-q mt-5">
      <h3 className="text-[12.5pt] font-bold text-[#0B2A4A] mb-2">{s.heading}</h3>
      {s.paragraphs.map((p) => <p key={p} className="text-[10.5pt] mb-1.5">{p}</p>)}
      {s.table.length > 0 && (
        <table className="print-table mt-2">
          <thead><tr>{s.table[0].map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>{s.table.slice(1).map((r) => <tr key={r[0]}>{r.map((c, i) => <td key={i}>{c}</td>)}</tr>)}</tbody>
        </table>
      )}
    </section>
  ));
}

function useOwnerProgress(filled, student) {
  const [state, setState] = useState({ progress: [], name: "" });
  const { user } = useAuth();
  useEffect(() => {
    if (!filled || !user) return;
    if (student) api.get(`/admin/students/${student}/print`).then(({ data }) => setState({ progress: data.progress, name: data.user.name }));
    else if (user.role !== "host") api.get("/course/progress").then(({ data }) => setState({ progress: data, name: user.name }));
  }, [filled, student, user]);
  return state;
}

export default function PrintPage() {
  const { kind, day } = useParams();
  const [params] = useSearchParams();
  const filled = params.get("mode") === "filled";
  const student = params.get("student");
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const { progress, name } = useOwnerProgress(filled, student);
  useSeo({ title: "Printable worksheets", path: "/print/worksheets", noindex: true });

  useEffect(() => {
    if (user === null) return;
    if (!user) {
      setError("Please sign in to print course pages.");
      return;
    }
    fetchCourse().then(setCourse).catch((e) => {
      setError(e.response?.status === 401 ? "Please sign in to print course pages." : e.response?.status === 403 ? "Course pages unlock after your application is approved." : formatApiError(e));
    });
  }, [kind, filled, user]);

  if (error) return <p className="text-center py-24 text-rose-600" data-testid="print-error">{error} <Link to="/login" className="underline">Sign in</Link></p>;
  if (!course) return <Spinner />;
  const byDay = Object.fromEntries(progress.map((p) => [p.day, p]));
  const who = filled ? name : "";
  const wsFor = (d) => course.worksheets.find((w) => w.id === `ws${d}`);
  let body;
  if (kind === "day" && course.days) {
    const d = course.days[Number(day) - 1];
    body = d ? <DaySheet day={d} ws={wsFor(d.day)} rec={byDay[d.day]} filled={filled} name={who} /> : null;
  } else if (kind === "host-plan" && user?.role !== "host") {
    body = <p className="text-rose-600" data-testid="print-error">The host lesson plan is available to the host only.</p>;
  } else if (kind === "host-plan" && course.days) {
    const days = day ? [course.days[Number(day) - 1]] : course.days;
    body = (
      <>
        {days.map((d, i) => <HostPlanDay key={d.day} day={d} breakBefore={i > 0} />)}
        {!day && <div className="print-break"><SheetHeader title="Host reference" name="Host" /><Reference sections={course.reference} /></div>}
      </>
    );
  } else {
    body = course.worksheets.map((ws, i) => <Worksheet key={ws.id} ws={ws} answers={byDay[ws.number]?.answers} filled={filled} name={who} breakBefore={i > 0} />);
  }
  const title = kind === "day" ? `Day ${day} worksheet` : kind === "host-plan" ? "Host lesson plan" : "Participant worksheets";

  return (
    <div className="print-root min-h-screen" data-testid="print-page">
      <div className="no-print sticky top-0 z-10 bg-[#0B2A4A] text-[#F5ECD7] px-4 sm:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = "/")} className="inline-flex items-center gap-1 text-sm font-semibold" data-testid="print-back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>
        <p className="text-sm">{title} · {filled ? "with answers" : "blank"} · US Letter</p>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-4 py-2 text-sm font-semibold" data-testid="print-download-pdf-btn"><Printer className="h-4 w-4" /> Print / Download PDF</button>
      </div>
      <p className="no-print text-center text-xs text-slate-500 py-2">To save a PDF, choose "Save as PDF" as the printer in the print dialog.</p>
      <div className="print-sheet">{body || <p>Not found.</p>}</div>
    </div>
  );
}
